<template>
  <v-card
    flat
    outlined
    rounded="lg"
    height="100%"
    color="cardbg"
    class="flex"
    elevation="10"
  >
    <v-card-title
      color="primary"
      :style="`padding: calc(${scale} * 0.4rem);
        line-height: calc(${scale} * 0.75rem);
        font-size: calc(${scale} * 0.7rem);`"
      class="font-weight-medium"
    >
      <div class="d-flex justify-space-between w-full titlecolor--text">
        <span
          class="d-flex align-center justify-space-between"
          :style="`font-size: calc(${scale} * 0.7rem); width: 100%;`"
        >
          <span class="mr-2">
          {{ title }}
          </span>
          <v-select
            hide-details
            append-icon=""
            :items="planParts"
            @change="partselect"
            v-model="selectedPart"
            color="success"
            style="font-size: inherit; max-width: 75%;"
            class="pa-0 ma-0 critical subtitlecolor--text"
          ></v-select>
        </span>
      </div>
    </v-card-title>
    <v-card-text
      :style="`padding: calc(${scale} * 0.25rem);`"
      style="height: 90%; line-height: normal;"
    >
      <WidgetLoading v-if="loading" />
      <span v-else>
        <div
          :style="`font-size: calc(${scale} * 0.5rem); line-height: 0;`"
          class="d-flex justify-space-between w-full subtitlecolor--text"
        >
          <span>REJ (NOS)</span>
          <span style="color: #21CE99;">REJ (%)</span>
        </div>
        <highcharts
          :key="uuid"
          :options="options"
          style="height: 100%; width: 100%;"
          ref="hh"
        ></highcharts>
      </span>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import WidgetLoading from './WidgetLoading.vue';

export default {
  name: 'ParetoChart',

  props: {
    title: { type: String },
    scale: { type: Number, default: 1 },
    unit: { type: String },
    loading: { type: Boolean, default: false },
    criticalpart: { type: Object },
    columncolor: { type: String, default: '#ffffff' }, // #495364
  },
  components: { WidgetLoading },
  data() {
    return {
      uuid: 'uuid',
      selectedPart: '',
      options: {
        chart: {
          type: 'column',
          style: {
            fontFamily: 'Poppins, "Open Sans", sans-serif',
          },
        },
        title: { text: '' },
        plotOptions: {
          series: {
            borderWidth: 0,
            dataLabels: {
              enabled: true,
              valueDecimals: 2,
              style: { fontSize: 10, color: '#ffffff', textOutline: 'none' },
            },
            style: {
              fontFamily: 'Poppins, "Open Sans", sans-serif',
            },
          },
        },
        tooltip: { shared: true },
        xAxis: {
          categories: [],
          labels: { style: { color: '#FFFFFF' } },
          type: 'category',
          crosshair: true,
          title: { text: '' },
        },
        yAxis: [
          {
            labels: { style: { color: '#FFFFFF' } },
            title: { text: '', style: { color: '#7AFC0B' } },
          }, {
            title: { text: '', style: { color: '#7AFC0B' } },
            minPadding: 0,
            maxPadding: 0,
            max: 100,
            min: 0,
            gridLineColor: '#646A7B',
            opposite: true,
            labels: { format: '{value}%', style: { color: '#FFFFFF' } },
          },
        ],
        legend: { enabled: false },
        series: [
          {
            name: 'Percentage',
            type: 'pareto',
            yAxis: 1,
            zIndex: 10,
            baseSeries: 1,
            color: '#65CE9A',
            dataLabels: { enabled: true, format: '{y:.2f}' },
            tooltip: { valueDecimals: 2, valueSuffix: '%' },
          }, {
            name: 'Rejections',
            type: 'column',
            color: this.columncolor,
            zIndex: 2,
            data: [],
          },
        ],
      },
    };
  },
  computed: {
    ...mapState('cellwiseQualityDashboard', ['planParts', 'theme', 'dataFromJSON']),
    ...mapGetters('cellwiseQualityDashboard', ['activeTheme']),
  },
  watch: {
    scale(val) {
      this.adjustSize(val);
      this.createUUID();
    },
    criticalpart(val) {
      if (!val) return;
      let selected = val.partname;
      const safePlanParts = Array.isArray(this.planParts) ? this.planParts : [];
      const normalizedTarget = this.normalizePartName(val.partname);
      const found = safePlanParts.find((part) => this.normalizePartName(part) === normalizedTarget);

      if (!found) {
        selected = safePlanParts.length > 0 ? safePlanParts[0] : '';
      } else {
        selected = found;
      }
      this.$nextTick(() => {
        this.selectedPart = selected;
      });
    },
  },
  mounted() {
    if (!this.criticalpart) {
      if (Array.isArray(this.planParts) && this.planParts.length > 0) {
        [this.selectedPart] = this.planParts;
      } else {
        this.selectedPart = '';
      }
    }
  },
  methods: {
    normalizePartName(value) {
      return String(value || '')
        .normalize('NFKD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase();
    },

    assignData(result) {
      this.options.xAxis.categories = result.categories;
      this.options.series[1].data = result.series;
      this.createUUID();
    },
    adjustSize(scale) {
      const lablesize = 8 * scale;
      const padding = 15 * scale;

      const currentTheme = this.activeTheme || 'dark';
      const subtitleColor = (this.theme && this.theme.themes && this.theme.themes[currentTheme])
        ? this.theme.themes[currentTheme].subtitlecolor
        : '#A1A3A5';

      const xlabels = {
        reserveSpace: true,
        y: padding,
        style: {
          color: subtitleColor,
          fontSize: lablesize,
          fontFamily: 'Poppins, "Open Sans", sans-serif',
        },
      };
      const ylables = {
        style: {
          color: subtitleColor,
          fontSize: lablesize,
          fontFamily: 'Poppins, "Open Sans", sans-serif',
        },
      };
      this.options.xAxis.labels = xlabels;
      this.options.yAxis[0].labels = ylables;
      this.options.yAxis[1].labels = ylables;
      if (this.options.plotOptions.series.dataLabels) {
        this.options.plotOptions.series.dataLabels.style = {
          ...this.options.plotOptions.series.dataLabels.style,
          fontSize: lablesize,
          color: '#ffffff',
          textOutline: 'none',
          fontFamily: 'Poppins, "Open Sans", sans-serif',
        };
      }
    },
    partselect() {
      if (!this.dataFromJSON) {
        this.$emit('selectpart', this.selectedPart);
      }
    },
    createUUID() {
      /* eslint-disable */
      var dt = new Date().getTime();
      var uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        var r = (dt + Math.random()*16)%16 | 0;
        dt = Math.floor(dt/16);
        return (c=='x' ? r :(r&0x3|0x8)).toString(16);
      });
      this.uuid =  uuid;
    },
  },
};
</script>
<style lang="scss">
.w-full {
  width: 100%;
}
.h-full {
  height: 100%;
}
.critical {
  input, .input-22, div {
    padding: 0 !important;
    margin: 0;
    line-height: inherit;
    display: flex;
    justify-content: right;
  }
  input {
    display: none;
  }
  .v-select__selections {
    line-height: inherit;
  }
  .v-input__append-inner {
    margin: 0;
    line-height: inherit;
  }
  .v-input__control v-input__slot::before {
    border-style: none !important;
  }
  .v-input__slot::before, .v-input__slot::after {
    border-style: none !important;
  }
}
</style>
